import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import Pagination from "../../components/Pagination"
import Modal from "../../components/Modal"
import Input from "../../components/Input"
import RowsLoader from "../../components/DataTables/RowsLoader"
import { api } from "../../api"
import { addAlertWithTimeout, AlertType } from "../../utils/alertUtils"

const emptyForm = {
  name: "",
  code: "",
  is_active: true,
}

const formatDate = value =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—"

const StatusLabel = ({ accessCode }) => {
  let text = "Active"
  let classes = "bg-green-100 text-green-700"

  if (!accessCode.is_active) {
    text = "Inactive"
    classes = "bg-gray-100 text-gray-500"
  } else if (!accessCode.is_redeemable) {
    text = "Expired"
    classes = "bg-orange-100 text-orange-700"
  }

  return (
    <span
      className={`text-xs py-1.5 px-4 rounded-full whitespace-nowrap ${classes}`}
    >
      {text}
    </span>
  )
}

const AccessCodeUsers = ({ accessCode }) => {
  const [users, setUsers] = useState(null)

  useEffect(() => {
    api()
      .loadAccessCodeUsers(accessCode.id)
      .then(response => setUsers(response?.data ?? []))
  }, [accessCode.id, accessCode.users_count])

  if (users === null) {
    return <p className="text-sm text-light-gray py-2">Loading users...</p>
  }

  if (users.length === 0) {
    return (
      <p className="text-sm text-light-gray py-2">
        Nobody has registered with this code yet.
      </p>
    )
  }

  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-light-gray">
          <td className="py-1.5 pr-3">Name</td>
          <td className="py-1.5 pr-3">Email</td>
          <td className="py-1.5 pr-3">Registered</td>
          <td></td>
        </tr>
      </thead>
      <tbody>
        {users.map(user => (
          <tr key={user.id} className="border-t border-gray-200">
            <td className="py-2 pr-3">{user.full_name || "—"}</td>
            <td className="py-2 pr-3 break-all">{user.email}</td>
            <td className="py-2 pr-3 whitespace-nowrap">
              {formatDate(user.created_at)}
            </td>
            <td className="py-2 text-right">
              <Link
                to={`/users/${user.id}`}
                className="text-link-color transition hover:opacity-70"
              >
                View user
              </Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const AccessCodesPage = () => {
  const [accessCodes, setAccessCodes] = useState(null)
  const [search, setSearch] = useState("")
  const [expandedId, setExpandedId] = useState(null)
  const [isModalShown, setIsModalShown] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [isSaving, setIsSaving] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [deleting, setDeleting] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const load = useCallback(() => {
    api()
      .loadAccessCodes()
      .then(response => setAccessCodes(response?.data ?? []))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()

    if (!accessCodes || !query) {
      return accessCodes
    }

    return accessCodes.filter(
      accessCode =>
        accessCode.name.toLowerCase().includes(query) ||
        accessCode.code.includes(query)
    )
  }, [accessCodes, search])

  const generateCode = () => {
    setIsGenerating(true)

    return api()
      .generateAccessCode()
      .then(response => {
        setIsGenerating(false)

        if (response?.code) {
          setForm(prev => ({ ...prev, code: response.code }))
        }
      })
  }

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setIsModalShown(true)
    generateCode()
  }

  const openEdit = accessCode => {
    setEditing(accessCode)
    setForm({
      name: accessCode.name,
      code: accessCode.code,
      is_active: accessCode.is_active,
    })
    setIsModalShown(true)
  }

  const closeModal = () => {
    setEditing(null)
    setForm(emptyForm)
  }

  const onSubmit = () => {
    setIsSaving(true)

    const request = editing
      ? api().updateAccessCode(editing.id, form)
      : api().createAccessCode(form)

    request.then(response => {
      setIsSaving(false)

      if (response?.data) {
        addAlertWithTimeout(
          "Success",
          <p>
            Access code <span className="font-bold">{response.data.name}</span>{" "}
            has been {editing ? "updated" : "created"}.
          </p>,
          AlertType.Success
        )

        setIsModalShown(false)
        closeModal()
        load()
      }
    })
  }

  const askToDelete = () => {
    setDeleting(editing)
    setIsModalShown(false)
    closeModal()
  }

  const onDelete = () => {
    setIsDeleting(true)

    api()
      .deleteAccessCode(deleting.id)
      .then(response => {
        setIsDeleting(false)

        if (response !== undefined) {
          addAlertWithTimeout(
            "Access code deleted",
            <p>
              Access code <span className="font-bold">{deleting.name}</span>{" "}
              has been deleted.
            </p>,
            AlertType.Warning
          )

          if (expandedId === deleting.id) {
            setExpandedId(null)
          }

          setDeleting(null)
          load()
        }
      })
  }

  const codeChanged = editing && form.code !== editing.code

  return (
    <>
      <Pagination data={[{ title: "Access Codes", link: "/access-codes" }]} />

      <div className="border-2 rounded-md bg-light-blue px-3">
        <div className="flex justify-between">
          <div className="mr-2 my-3 w-full">
            <input
              type="search"
              className="block w-full p-2 text-sm text-gray-900 border border-gray-300 rounded-md bg-gray-100 transition duration-200 focus:bg-white outline-0"
              placeholder="Search by name or code"
              autoComplete="off"
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
          </div>
          <button
            onClick={openCreate}
            className="block text-center py-2 px-4 my-3 w-32 text-sm text-gray-900 border border-gray-300 rounded-md bg-gray-100 transition hover:bg-white focus:bg-white"
          >
            Add new
          </button>
        </div>

        <div className="overflow-x-auto visible-horizontal-scrollbar mb-2.5 min-h-[450px]">
          <table className="w-full border-spacing-y-1.5 border-separate border-t border-gray-300">
            <thead>
              <tr>
                {["Name", "Code", "Status", "Users", "Created", ""].map(
                  (title, index) => (
                    <td
                      key={index}
                      className="px-3 py-2.5 text-sm text-light-gray"
                    >
                      {title}
                    </td>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {filtered === null ? (
                <RowsLoader columnsCount={6} rowsCount={5} />
              ) : filtered.length === 0 ? (
                <tr className="bg-white">
                  <td
                    colSpan={6}
                    className="rounded-md py-7 px-3 text-light-gray text-center"
                  >
                    {accessCodes.length === 0
                      ? "No access codes yet"
                      : "Nothing found"}
                  </td>
                </tr>
              ) : (
                filtered.map(accessCode => {
                  const isExpanded = expandedId === accessCode.id

                  return (
                    <React.Fragment key={accessCode.id}>
                      <tr className="bg-white">
                        <td className="rounded-l-md p-5 font-medium">
                          {accessCode.name}
                        </td>
                        <td className="p-5 font-mono tracking-widest">
                          {accessCode.code}
                        </td>
                        <td className="p-5">
                          <StatusLabel accessCode={accessCode} />
                        </td>
                        <td className="p-5">
                          <button
                            onClick={() =>
                              setExpandedId(isExpanded ? null : accessCode.id)
                            }
                            className="text-link-color transition hover:opacity-70 whitespace-nowrap"
                          >
                            {accessCode.users_count}{" "}
                            {accessCode.users_count === 1 ? "user" : "users"}{" "}
                            {isExpanded ? "▴" : "▾"}
                          </button>
                        </td>
                        <td className="p-5 whitespace-nowrap">
                          {formatDate(accessCode.created_at)}
                        </td>
                        <td className="rounded-r-md p-5 text-center">
                          <button
                            onClick={() => openEdit(accessCode)}
                            className="text-link-color text-sm transition hover:opacity-70"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-white">
                          <td colSpan={6} className="rounded-md px-5 pb-4">
                            <AccessCodeUsers accessCode={accessCode} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-light-gray pb-2">
          Total records: {accessCodes ? accessCodes.length : "..."}
        </p>
      </div>

      <Modal
        title={editing ? "Edit Access Code" : "Add new Access Code"}
        size="lg"
        isShown={isModalShown}
        setIsShown={setIsModalShown}
        onSubmit={onSubmit}
        onClose={closeModal}
        submitActionActive={form.name.trim() !== "" && form.code !== ""}
        submitActionLoading={isSaving}
        leftButtonText={editing ? "Delete" : ""}
        leftButtonAction={askToDelete}
        leftButtonClassNames="!text-red-500"
      >
        <div className="mb-3">
          Name:
          <Input
            id="name"
            onChange={event => setForm({ ...form, name: event.target.value })}
            value={form.name}
            placeholder="e.g. Africa"
          />
        </div>
        <div className="mb-3">
          Code:
          <div className="flex">
            <Input
              id="code"
              className="font-mono tracking-widest mr-2"
              inputMode="numeric"
              maxLength={6}
              onChange={event =>
                setForm({
                  ...form,
                  code: event.target.value.replace(/\D/g, ""),
                })
              }
              value={form.code}
              placeholder="6 digits"
            />
            <button
              type="button"
              onClick={generateCode}
              disabled={isGenerating}
              className="whitespace-nowrap px-4 text-sm border border-gray-300 rounded bg-gray-100 transition hover:bg-white disabled:opacity-50"
            >
              {isGenerating ? "..." : "Generate"}
            </button>
          </div>
          {codeChanged && (
            <p className="text-sm text-orange-700 mt-2">
              The old code {editing.code} will stop working. The{" "}
              {editing.users_count} users already registered with it stay
              attached.
            </p>
          )}
        </div>
        {editing && (
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="mr-2"
              checked={form.is_active}
              onChange={event =>
                setForm({ ...form, is_active: event.target.checked })
              }
            />
            Active — new users can register with this code
          </label>
        )}
      </Modal>

      <Modal
        title="Delete Access Code"
        size="md"
        isShown={deleting !== null}
        setIsShown={isShown => !isShown && setDeleting(null)}
        onSubmit={onDelete}
        submitText="Delete"
        submitActionLoading={isDeleting}
      >
        {deleting && (
          <>
            <p>
              Are you sure you want to delete{" "}
              <span className="font-bold">{deleting.name}</span> (
              <span className="font-mono">{deleting.code}</span>)?
            </p>
            <p className="text-sm text-light-gray mt-2">
              {deleting.users_count > 0
                ? `The ${deleting.users_count} ${
                    deleting.users_count === 1 ? "user" : "users"
                  } registered with it keep their accounts, they just will no longer be tagged with this code.`
                : "Nobody has registered with this code."}
            </p>
          </>
        )}
      </Modal>
    </>
  )
}

export default AccessCodesPage
